import React, { useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwTextArea from "@components/shared/OwTextArea/OwTextArea";
import OwButton from "@components/shared/OwButton/OwButton";
import { useRmaDetail } from "@hooks/rma/useRmaDetail";
import { RmaProgressTracker } from "@components/shared/RmaProgressTracker/RmaProgressTracker";
import { RmaDetailHeader } from "../RmaDetailHeader/RmaDetailHeader";
import { RmaActionButtons } from "../RmaActionButtons/RmaActionButtons";
import { RmaLineItemsSection } from "../RmaLineItemsSection/RmaLineItemsSection";
import { RmaInspectionsSection } from "../RmaInspectionsSection/RmaInspectionsSection";
import { RmaStatusHistory } from "../RmaStatusHistory/RmaStatusHistory";
import { ReceiveItemsModal } from "../ReceiveItemsModal/ReceiveItemsModal";
import { RecordInspectionModal } from "../RecordInspectionModal/RecordInspectionModal";
import { EditInspectionModal } from "../EditInspectionModal/EditInspectionModal";
import { ProcessRmaModal } from "../ProcessRmaModal/ProcessRmaModal";
import IBreadcrumbs from "@models/IBreadcrumbs";
import RmaStatusEnum from "@models/RmaStatusEnum";
import RmaService from "@services/RmaService";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import "./RmaDetail.scss";
import IRMA_Line_Item from "@models/IRMA_Line_Item";
import IRMA_Line_Item_Inspection from "@models/IRMA_Line_Item_Inspection";
import NotificationLevelEnum from "@models/NotificationLevelEnum";

type Props = {};

export const RmaDetail = (props: Props) => {
    const { guid } = useParams<{ guid: string }>();
    const navigate = useNavigate();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const { rma, loading, error, refetch } = useRmaDetail(guid || "");

    const [editMode, setEditMode] = useState<boolean>(false);
    const [editedNotes, setEditedNotes] = useState<string>("");
    const [editedTrackingNumber, setEditedTrackingNumber] = useState<string>("");
    const [actionLoading, setActionLoading] = useState<boolean>(false);

    // Modal states
    const [receiveItemsModalOpen, setReceiveItemsModalOpen] = useState<boolean>(false);
    const [recordInspectionModalOpen, setRecordInspectionModalOpen] = useState<boolean>(false);
    const [editInspectionModalOpen, setEditInspectionModalOpen] = useState<boolean>(false);
    const [processRmaModalOpen, setProcessRmaModalOpen] = useState<boolean>(false);
    const [selectedLineItem, setSelectedLineItem] = useState<IRMA_Line_Item | null>(null);
    const [selectedInspection, setSelectedInspection] = useState<IRMA_Line_Item_Inspection | null>(null);

    const breadcrumbs: IBreadcrumbs[] = [
        { title: "rma", url: "/inventory/rma" },
        { url: `/inventory/rma/${guid}`, title: rma?.rma_number || guid || "", skipTranslate: true },
    ];

    const handleEditToggle = () => {
        if (!editMode && rma) {
            setEditedNotes(rma.notes || "");
            setEditedTrackingNumber(rma.tracking_number || "");
        }
        setEditMode(!editMode);
    };

    const handleSaveEdit = async () => {
        if (!rma || !guid) return;

        try {
            setActionLoading(true);
            await RmaService.updateRma(
                guid,
                {
                    notes: editedNotes,
                    tracking_number: editedTrackingNumber,
                },
                userModel,
                supplierModel
            );
            uiContext.showNotification({
                message: "RMA updated successfully",
                level: NotificationLevelEnum.success,
            });
            setEditMode(false);
            refetch();
        } catch (err) {
            uiContext.showNotification({
                message: err instanceof Error ? err.message : "Failed to update RMA",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleAuthorize = async () => {
        if (!rma || !guid) return;

        try {
            setActionLoading(true);
            await RmaService.authorizeRma(guid, userModel, supplierModel);
            uiContext.showNotification({
                message: "RMA authorized successfully",
                level: NotificationLevelEnum.success,
            });
            refetch();
        } catch (err) {
            uiContext.showNotification({
                message: err instanceof Error ? err.message : "Failed to authorize RMA",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!rma || !guid) return;

        if (window.confirm("Are you sure you want to cancel this RMA? This action cannot be undone.")) {
            try {
                setActionLoading(true);
                await RmaService.cancelRma(guid, userModel, supplierModel);
                uiContext.showNotification({
                    message: "RMA cancelled successfully",
                    level: NotificationLevelEnum.success,
                });
                refetch();
            } catch (err) {
                uiContext.showNotification({
                    message: err instanceof Error ? err.message : "Failed to cancel RMA",
                    level: NotificationLevelEnum.error,
                });
            } finally {
                setActionLoading(false);
            }
        }
    };

    const handleDelete = async () => {
        if (!rma || !guid) return;

        if (window.confirm("Are you sure you want to delete this RMA? This action cannot be undone.")) {
            try {
                setActionLoading(true);
                await RmaService.deleteRma(guid, userModel, supplierModel);
                uiContext.showNotification({
                    message: "RMA deleted successfully",
                    level: NotificationLevelEnum.success,
                });
                navigate("/inventory/rma");
            } catch (err) {
                uiContext.showNotification({
                    message: err instanceof Error ? err.message : "Failed to delete RMA",
                    level: NotificationLevelEnum.error,
                });
            } finally {
                setActionLoading(false);
            }
        }
    };

    const handleReceiveItems = () => {
        setReceiveItemsModalOpen(true);
    };

    const handleRecordInspection = () => {
        setRecordInspectionModalOpen(true);
    };

    const handleAddInspection = (lineItem: IRMA_Line_Item) => {
        setSelectedLineItem(lineItem);
        setRecordInspectionModalOpen(true);
    };

    const handleProcess = () => {
        setProcessRmaModalOpen(true);
    };

    const handleEditInspection = (lineItem: IRMA_Line_Item, inspection: IRMA_Line_Item_Inspection) => {
        setSelectedLineItem(lineItem);
        setSelectedInspection(inspection);
        setEditInspectionModalOpen(true);
    };

    const handleDeleteInspection = async (lineItem: IRMA_Line_Item, inspection: IRMA_Line_Item_Inspection) => {
        if (!rma || !guid) return;

        if (window.confirm("Are you sure you want to delete this inspection?")) {
            try {
                setActionLoading(true);
                await RmaService.deleteInspection(guid, lineItem.guid, inspection.guid, userModel, supplierModel);
                uiContext.showNotification({
                    message: "Inspection deleted successfully",
                    level: NotificationLevelEnum.success,
                });
                refetch();
            } catch (err) {
                uiContext.showNotification({
                    message: err instanceof Error ? err.message : "Failed to delete inspection",
                    level: NotificationLevelEnum.error,
                });
            } finally {
                setActionLoading(false);
            }
        }
    };

    if (loading) {
        return <div className="RmaDetail">Loading...</div>;
    }

    if (error || !rma) {
        return (
            <div className="RmaDetail">
                <div className="alert alert-danger">{error || "Failed to load RMA"}</div>
            </div>
        );
    }

    const canEdit = rma.rma_status_id === RmaStatusEnum.DRAFT;

    return (
        <div className="RmaDetail">
            <Breadcrumbs crumbs={breadcrumbs} />

            <RmaDetailHeader rma={rma} />

            <div className="d-flex justify-content-end">
                <RmaActionButtons
                    rma={rma}
                    onAuthorize={handleAuthorize}
                    onReceiveItems={handleReceiveItems}
                    onRecordInspection={handleRecordInspection}
                    onProcess={handleProcess}
                    onCancel={handleCancel}
                    onEdit={handleEditToggle}
                    onDelete={handleDelete}
                    loading={actionLoading}
                />
            </div>

            <div className="d-flex w-100 justify-content-center align-items-center mb-4">
                <RmaProgressTracker rma={rma} />
            </div>

            <Tabs>
                <TabList>
                    <Tab>
                        <OwLabel title="details" noBottomMargin={true} />
                    </Tab>
                    <Tab>
                        <OwLabel title="history" noBottomMargin={true} />
                    </Tab>
                </TabList>

                <TabPanel>
                    <div className="details-tab-content mt-3 p-2">
                        <div className="row">
                            <div className="col-md-6">
                                <OwLabel title="notes" isBold={true} />
                                {editMode && canEdit ? (
                                    <OwTextArea name="notes" value={editedNotes} fn={(e) => setEditedNotes(e.target.value)} rows={4} />
                                ) : (
                                    <div>{rma.notes || "-"}</div>
                                )}
                            </div>
                            <div className="col-md-6">
                                <OwLabel title="tracking" isBold={true} />
                                {editMode && canEdit ? (
                                    <OwInputWrapper
                                        name="tracking_number"
                                        value={editedTrackingNumber}
                                        fn={(e: any) => setEditedTrackingNumber(e.target.value)}
                                        placeholder="tracking"
                                    />
                                ) : (
                                    <div>{rma.tracking_number || "-"}</div>
                                )}
                            </div>
                        </div>

                        {editMode && canEdit && (
                            <div className="mt-3">
                                <OwButton title="save" fn={handleSaveEdit} active={actionLoading} className="btn-primary me-2" />
                                <OwButton title="cancel" fn={handleEditToggle} active={actionLoading} className="btn-secondary me-2" />
                            </div>
                        )}

                        <div className="row mt-4">
                            <div className="col-md-4">
                                <OwLabel title="created_by" isBold={true} />
                                <div>{rma.created_by_user?.name || "-"}</div>
                            </div>
                            {rma.authorized_by_user && (
                                <div className="col-md-4">
                                    <OwLabel title="authorized_by" isBold={true} />
                                    <div>{rma.authorized_by_user.name}</div>
                                </div>
                            )}
                            {rma.total_refund_amount !== null && (
                                <div className="col-md-4">
                                    <OwLabel title="total_refund_amount" isBold={true} />
                                    <div>${Number(rma.total_refund_amount || 0).toFixed(2)}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </TabPanel>

                <TabPanel>
                    <div className="history-tab-content mt-3 p-2">
                        <RmaStatusHistory statusHistories={rma.RMA_Status_Histories || []} />
                    </div>
                </TabPanel>
            </Tabs>

            <RmaLineItemsSection rma={rma} onAddInspection={handleAddInspection} />

            <RmaInspectionsSection rma={rma} onEdit={handleEditInspection} onDelete={handleDeleteInspection} loading={actionLoading} />

            {/* Modals */}
            <ReceiveItemsModal
                rma={rma}
                isOpen={receiveItemsModalOpen}
                onClose={() => setReceiveItemsModalOpen(false)}
                onSuccess={refetch}
            />

            <RecordInspectionModal
                rma={rma}
                lineItem={selectedLineItem}
                isOpen={recordInspectionModalOpen}
                onClose={() => {
                    setRecordInspectionModalOpen(false);
                    setSelectedLineItem(null);
                }}
                onSuccess={refetch}
            />

            <EditInspectionModal
                rma={rma}
                lineItem={selectedLineItem}
                inspection={selectedInspection}
                isOpen={editInspectionModalOpen}
                onClose={() => {
                    setEditInspectionModalOpen(false);
                    setSelectedLineItem(null);
                    setSelectedInspection(null);
                }}
                onSuccess={refetch}
            />

            <ProcessRmaModal rma={rma} isOpen={processRmaModalOpen} onClose={() => setProcessRmaModalOpen(false)} onSuccess={refetch} />
        </div>
    );
};
